import React, { useEffect, useState, useRef } from 'react';
import { generateUid } from '../../../utils/generateUid';
import { listSchedules, listTimeZones, getSchedule } from '../../../services/configuration/configuration.service';
import type { ScheduleSummary, ScheduleDetail, TimeZoneSummary } from '../../../services/configuration/configuration.types';
import type { CreateRecipientRequest } from '../../../services/distribution/distribution.types';
import { FormField } from '../../ui/FormField';
import RecipientRow from '../distributionGroups/form-components/RecipientRow';
import recipientValidation from './form-components/recipientValidation';
import { validators } from '../../../helpers/validators';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { StatusMessage } from '../../ui';
import { FormErrorSummary } from '../../ui/FormErrorSummary';

interface DistributionGroupFormData {
    profileName: string;
    recipients?: CreateRecipientRequest[];
}

interface DistributionGroupsFormProps {
    onSubmit: (data: DistributionGroupFormData) => void | boolean | Promise<void | boolean>;
    onCancel?: () => void;
    initialData?: Partial<DistributionGroupFormData>;
    isLoading?: boolean;
    groupId?: number;
    onDeleteRecipient?: (recipientId: number) => Promise<void>;
}

type RecipientRow = Omit<Partial<CreateRecipientRequest>, 'firstName' | 'lastName' | 'email' | 'sms'> & {
    __uid: string;
    firstName: string;
    lastName: string;
    email: string;
    sms: string;
    isEditing?: boolean;
    contactLabel?: string;
    bureauContactType?: string;
    origIndex?: number;
    scheduleId?: number | null;
    timeZoneId?: number | null;
    profileName: string;
};

type RecipientRowData = {
    firstName?: string;
    lastName?: string;
    scheduleId?: number | null;
    timeZoneId?: number | null;
    contactLabel?: string;
    isBureauRecipient: boolean;
    email?: string;
    sms?: string;
    recipientId?: number | null;
};

type RecipientWithUiFields = CreateRecipientRequest & {
    email?: string;
    sms?: string;
    contactLabel?: string;
    emailRecipientId?: number;
    smsRecipientId?: number;
    recipientId?: number;
};

type InitialDistributionGroupData = Partial<DistributionGroupFormData> & {
    hasBureauRecipients?: boolean;
    recipients?: RecipientWithUiFields[];
};

type RecipientFieldName = 'profileName' | 'email' | 'sms' | 'firstName' | 'lastName' | 'scheduleId' | 'timeZoneId';
type RecipientFieldEvent = React.ChangeEvent<HTMLInputElement | HTMLSelectElement>;
type RecipientChangeValue = RecipientRow[keyof RecipientRow];

const RECIPIENT_NAME_MAX_LENGTH = 50;
const ERROR_SUMMARY_SCROLL_DELAY_MS = 80;

const getValidationRules = () => ({
    profileName: validators.applyToFirstRowOnly([
        validators.required('Profile name is required'),
        validators.minLength(2, 'Profile name must be at least 2 characters'),
        validators.maxLength(100, 'Profile name must be less than 100 characters'),
    ]),
    firstName: validators.applyToNonFirstRowUnlessBureauOnly([
        validators.required("First name is required"),
        validators.minLength(2, "First name minimum length is 2 characters"),
        validators.maxLength(RECIPIENT_NAME_MAX_LENGTH, "First name maximum length is 50 characters"),
    ]),
    lastName: validators.applyToNonFirstRowUnlessBureauOnly([
        validators.required("Last name is required"),
        validators.minLength(2, "Last name minimum length is 2 characters"),
        validators.maxLength(RECIPIENT_NAME_MAX_LENGTH, "Last name maximum length is 50 characters"),
    ]),
    scheduleId: [],
    email: validators.applyToNonFirstRowUnlessBureauOnly([
        validators.email("Enter valid email"),
        validators.emailOrSmsRequired('email'),
    ]),
    sms: validators.applyToNonFirstRowUnlessBureauOnly([
        validators.pattern(/^[0-9+*]+$/, "Only numbers and + or * are allowed"),
        validators.emailOrSmsRequired('sms'),
    ]),
    timeZoneId: validators.applyToNonFirstRowOnly([validators.requiredSelection("Time Zone is required")]),
});

const buildInitialRecipients = (initialData: InitialDistributionGroupData): RecipientRow[] => {
    const initialRecipientsRaw = initialData.recipients ?? [];
    let prepared: RecipientRow[] = [];
    if (initialRecipientsRaw.length > 0) {
        prepared = initialRecipientsRaw.map((r: RecipientWithUiFields) => ({
            ...r,
            __uid: generateUid(),
            firstName: r.firstName ?? '',
            lastName: r.lastName ?? '',
            email: r.email ?? '',
            sms: r.sms ?? '',
            contactLabel: r.contactLabel,
            isBureauRecipient: r.isBureauRecipient,
            scheduleId: r.scheduleId ?? null,
            timeZoneId: r.timeZoneId ?? null,
            profileName: initialData.profileName ?? '',
        }));
    }

    if (prepared.length === 0 && initialData.hasBureauRecipients === true) {
        prepared.push({
            __uid: generateUid(),
            firstName: '',
            lastName: '',
            email: '',
            sms: '',
            isBureauRecipient: true,
            bureauContactType: 'bureau',
            scheduleId: initialData.recipients?.[0]?.scheduleId ?? null,
            timeZoneId: initialData.recipients?.[0]?.timeZoneId ?? null,
            profileName: initialData.profileName ?? '',
        });
    }

    return prepared;
};

const useSchedulesAndTimeZones = () => {
    const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [timeZones, setTimeZones] = useState<TimeZoneSummary[]>([]);

    useEffect(() => {
        let mounted = true;
        setSchedulesLoading(true);
        listSchedules()
            .then(res => {
                if (mounted && res?.success && res.data) {
                    setSchedules(res.data);
                }
            })
            .catch(() => { })
            .finally(() => { if (mounted) {setSchedulesLoading(false); }});

        listTimeZones()
            .then(res => {
                if (mounted && res?.success && res.data) {setTimeZones(res.data);}
            })
            .catch(() => { });

        return () => { mounted = false; };
    }, []);

    return { schedules, schedulesLoading, timeZones };
};

const useErrorSummaryScroll = (showErrorSummary: boolean, ref: React.RefObject<HTMLDivElement | null>) => {
    useEffect(() => {
        if (!showErrorSummary) { return; }

        setTimeout(() => {
            try {
                ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (e) {
                console.info('[DistributionGroupsForm] scrollIntoView failed:', e);
            }
        }, ERROR_SUMMARY_SCROLL_DELAY_MS);
    }, [showErrorSummary, ref]);
};

const renderErrorSummaryContent = (
    showErrorSummary: boolean,
    errors: Array<Record<string, string | undefined>>,
    onClose: () => void,
): React.ReactNode => {
    if (!showErrorSummary || errors.length === 0) {
        return null;
    }

    const uniqueErrors = [...new Set(
        errors.flatMap(err => Object.values(err).filter(Boolean))
    )] as string[];

    if (uniqueErrors.length === 0) {
        return null;
    }

    return (
        <StatusMessage kind="error" onClose={onClose}>
            <FormErrorSummary errors={uniqueErrors} />
        </StatusMessage>
    );
};

const loadScheduleIfMissing = (
    scheduleId: number,
    cacheRef: { current: Record<number, ScheduleDetail> },
): void => {
    if (cacheRef.current[scheduleId]) {
        return;
    }
    getSchedule(scheduleId)
        .then(r => {
            if (r?.success && r.data) {
                cacheRef.current[scheduleId] = r.data;
            }
        })
        .catch(() => { });
};

type RenderRecipientsParams = {
    isEditMode: boolean;
    formData: DistributionGroupFormData;
    errors: Array<Record<string, string | undefined>>;
    schedules: ScheduleSummary[];
    timeZones: TimeZoneSummary[];
    handleRecipientFieldChange: (index: number, field: RecipientFieldName) => (e: RecipientFieldEvent) => void;
    handleRecipientChange: (uid: string, field: string, value: RecipientChangeValue) => void;
    handleRemoveRecipient: (uid: string, index: number) => Promise<void>;
    sanitizeRecipientRow: (r: CreateRecipientRequest, idx: number) => RecipientRow;
};

const renderRecipientsContent = ({
    isEditMode,
    formData,
    errors,
    schedules,
    timeZones,
    handleRecipientFieldChange,
    handleRecipientChange,
    handleRemoveRecipient,
    sanitizeRecipientRow,
}: RenderRecipientsParams): React.ReactNode => {
    const recipients = formData.recipients ?? [];
    const sanitized = recipients.map((r, idx) => sanitizeRecipientRow(r, idx));

    if (isEditMode) {
        return sanitized.map((r: RecipientRow) => (
            <RecipientRow
                key={r.__uid}
                r={r}
                origIndex={r.origIndex ?? 0}
                errors={errors}
                schedules={schedules}
                timeZones={timeZones}
                handleChangeFormField={handleRecipientFieldChange}
                handleRecipientChange={handleRecipientChange}
                handleRemoveRecipient={handleRemoveRecipient}
                variant="entry"
            />
        ));
    }

    const displayRecipients = sanitized.filter(r => !r.isEditing);
    const editingRecipients = sanitized.filter(r => r.isEditing);

    return (
        <>
            {displayRecipients.length > 0 && (
                <div className="table-responsive mb-3">
                    <table className="table table-sm distribution">
                        <thead>
                            <tr>
                                <th>CSH Recipient</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Schedule</th>
                                <th>Email</th>
                                <th>SMS</th>
                                <th>Time Zone</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayRecipients.map((r: RecipientRow) => (
                                <RecipientRow
                                    key={r.__uid}
                                    r={r}
                                    origIndex={r.origIndex ?? 0}
                                    errors={errors}
                                    schedules={schedules}
                                    timeZones={timeZones}
                                    handleChangeFormField={handleRecipientFieldChange}
                                    handleRecipientChange={handleRecipientChange}
                                    handleRemoveRecipient={handleRemoveRecipient}
                                    variant="table"
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {editingRecipients.map((r: RecipientRow) => (
                <RecipientRow
                    key={r.__uid}
                    r={r}
                    origIndex={r.origIndex ?? 0}
                    errors={errors}
                    schedules={schedules}
                    timeZones={timeZones}
                    handleChangeFormField={handleRecipientFieldChange}
                    handleRecipientChange={handleRecipientChange}
                    handleRemoveRecipient={handleRemoveRecipient}
                    variant="entry"
                />
            ))}
        </>
    );
};

export const DistributionGroupsForm: React.FC<DistributionGroupsFormProps> = ({
    onSubmit,
    onCancel,
    initialData = {},
    isLoading = false,
    groupId,
    onDeleteRecipient
}) => {
    const initialDistributionData = initialData as InitialDistributionGroupData;

    const { errors, handleChangeFormField, resetErrors, validateAll } =
        useFormValidation({
            initialValues: [{
                profileName: "",
                firstName: "",
                lastName: "",
                scheduleId: "0",
                email: "",
                sms: "",
                timeZoneId: 1,
            }],
            rules: getValidationRules()
        });

    const initialRecipientsPrepared = buildInitialRecipients(initialDistributionData);

    const [formData, setFormData] = useState<DistributionGroupFormData>({
        profileName: initialData.profileName ?? '',
        recipients: initialRecipientsPrepared as CreateRecipientRequest[]
    });

    const { schedules, schedulesLoading, timeZones } = useSchedulesAndTimeZones();

    const [showErrorSummary, setShowErrorSummary] = useState(false);
    const errorSummaryRef = useRef<HTMLDivElement | null>(null);

    const isEditMode = Boolean(typeof groupId === 'number' && groupId > 0);
    const scheduleDetailCacheRef = useRef<Record<number, ScheduleDetail>>({});

    useErrorSummaryScroll(showErrorSummary, errorSummaryRef);

    // Default null scheduleId/timeZoneId to first available option when schedules/timeZones load
    useEffect(() => {
        if (schedules.length > 0 && timeZones.length > 0) {
            setFormData(prev => {
                const updatedRecipients = (prev.recipients ?? []).map(r => ({
                    ...r,
                    scheduleId: r.scheduleId ?? schedules[0].id,
                    timeZoneId: r.timeZoneId ?? timeZones[0].id,
                }));
                return { ...prev, recipients: updatedRecipients };
            });
        }
    }, [schedules, timeZones]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target as HTMLInputElement;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRecipientChange = (uid: string, field: string, value: RecipientChangeValue) => {
        setFormData(prev => {
            const recipients: RecipientRow[] = (prev.recipients ?? []).map((r: CreateRecipientRequest) => ({ ...r as RecipientRow }));
            const i = recipients.findIndex((c: RecipientRow) => c.__uid === uid);
            if (i < 0) {
                return { ...prev, recipients: recipients as CreateRecipientRequest[] };
            }
            recipients[i] = { ...recipients[i], [field]: value };
            if (field === 'scheduleId' && value) {
                loadScheduleIfMissing(Number(value), scheduleDetailCacheRef);
            }
            return { ...prev, recipients: recipients as CreateRecipientRequest[] };
        });
    };

    const handleRecipientFieldChange = handleChangeFormField as (index: number, field: RecipientFieldName) => (e: RecipientFieldEvent) => void;

    function handleProfileNameChange(e: RecipientFieldEvent) {
        handleRecipientFieldChange(0, 'profileName')(e);
        handleChange(e);
    }

    const sanitizeRecipientRow = (r: CreateRecipientRequest, origIndex: number): RecipientRow => {
        const sanitized = { ...r as RecipientRow, origIndex };
        if (sanitized.scheduleId === null || sanitized.scheduleId === undefined) {
            sanitized.scheduleId = schedules.length > 0 ? schedules[0].id : null;
        }
        if (sanitized.timeZoneId === null || sanitized.timeZoneId === undefined) {
            sanitized.timeZoneId = timeZones.length > 0 ? timeZones[0].id : null;
        }
        return sanitized;
    };

    const addProfileNameToRows = (rows: RecipientRow[], profileName: string): any[] => {
        return rows.map(r => ({
            ...r,
            profileName: profileName ?? '',
            firstName: r.firstName ?? '',
            lastName: r.lastName ?? '',
            email: r.email ?? '',
            sms: r.sms ?? '',
            scheduleId: r.scheduleId === undefined ? '' : String(r.scheduleId),
            timeZoneId: r.timeZoneId === undefined ? 0 : Number(r.timeZoneId)
        }));
    };

    const validateRowsAndShow = (rows: RecipientRow[]): boolean => {
        const { ok } = recipientValidation.validateRows(addProfileNameToRows(rows, formData.profileName ?? ''), validateAll);
        setShowErrorSummary(!ok);
        return ok;
    };

    const toOptionalNumber = (val: number | null | undefined): number | undefined => {
        if (val !== undefined && val !== null) {
            return Number(val);
        }
        return undefined;
    };

    const extractRecipientData = (r: RecipientRow): RecipientRowData => ({
        firstName: r.firstName,
        lastName: r.lastName,
        scheduleId: toOptionalNumber(r.scheduleId),
        timeZoneId: toOptionalNumber(r.timeZoneId),
        contactLabel: r.contactLabel,
        isBureauRecipient: r.isBureauRecipient === true,
        email: r.email,
        sms: r.sms,
        recipientId:r.recipientId
    });

    const buildRecipientsFromRows = (rows: RecipientRow[]): CreateRecipientRequest[] => {
        const recipients: CreateRecipientRequest[] = [];
        const recipientType = (isBureau: boolean): 'BUREAU' | 'PERSON' => { 
            if (isBureau) {
                return 'BUREAU';
            }
            return 'PERSON';
        };

        for (const r of rows) {
            const data = extractRecipientData(r);
            const hasEmail = !!data.email;
            const hasSms = !!data.sms;

            if (hasEmail && data.email) {
                recipients.push({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    contactType: 'email',
                    contactValue: data.email,
                    contactLabel: data.contactLabel,
                    scheduleId: data.scheduleId ?? null,
                    timeZoneId: data.timeZoneId ?? null,
                    recipientType: recipientType(data.isBureauRecipient),
                    isBureauRecipient: data.isBureauRecipient,
                    recipientId: data.recipientId || null,
                });
            }
            if (hasSms && data.sms) {
                recipients.push({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    contactType: 'sms',
                    contactValue: data.sms,
                    contactLabel: data.contactLabel,
                    scheduleId: data.scheduleId ?? null,
                    timeZoneId: data.timeZoneId ?? null,
                    recipientType: recipientType(data.isBureauRecipient),
                    isBureauRecipient: data.isBureauRecipient,
                    recipientId: data.recipientId || null,
                });
            }
            if (data.isBureauRecipient && !hasEmail && !hasSms) {
                recipients.push({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    contactType: 'bureau',
                    contactValue: '',
                    contactLabel: data.contactLabel,
                    scheduleId: data.scheduleId ?? null,
                    timeZoneId: data.timeZoneId ?? null,
                    recipientType: 'BUREAU',
                    isBureauRecipient: data.isBureauRecipient,
                    recipientId: data.recipientId || null
                });
            }
        }
        return recipients;
    };

    const handleAddRecipient = () => {
        const ok = validateRowsAndShow([{ __uid: generateUid(), profileName: formData.profileName ?? '', firstName: '', lastName: '', email: '', sms: '' }]);
        if (!ok) { return; }

        const newRow: RecipientRow = {
            __uid: generateUid(),
            firstName: '',
            lastName: '',
            email: '',
            sms: '',
            contactLabel: '',
            scheduleId: schedules.length > 0 ? schedules[0].id : null,
            timeZoneId: timeZones.length > 0 ? timeZones[0].id : null,
            isEditing: true,
            profileName: formData.profileName ?? ''
        };

        setFormData(prev => ({
            ...prev,
            recipients: [ ...(prev.recipients ?? []), newRow as CreateRecipientRequest ]
        }));
    };

    const handleCloseStatusMessage = () => {
        setShowErrorSummary(false);
    };

    const handleRemoveRecipient = async (uid: string, index: number) => {
        setShowErrorSummary(false);

        if (index === 0) {
            resetErrors();
        }

        const recipient = (formData.recipients ?? []).find((r: CreateRecipientRequest) => (r as RecipientRow).__uid === uid);

        if (groupId && onDeleteRecipient && recipient) {
            const recipientRow = recipient as RecipientRow & RecipientWithUiFields;

            const recipientIds = [recipientRow.emailRecipientId, recipientRow.smsRecipientId, recipientRow.recipientId]
                .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id));
            const uniqueRecipientIds = Array.from(new Set(recipientIds));

            for (const rid of uniqueRecipientIds) {
                try {
                    await onDeleteRecipient(rid);
                } catch (err) {
                    console.info('Failed to delete recipient:', err);
                    return;
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            recipients: (prev.recipients ?? []).filter((r: CreateRecipientRequest) => (r as RecipientRow).__uid !== uid)
        }));
    };

    const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        const rowsForValidation: RecipientRow[] = [
            { __uid: generateUid(), profileName: formData.profileName ?? '', firstName: '', lastName: '', email: '', sms: '' },
            ...((formData.recipients ?? []) as RecipientRow[]).map(r => ({
                ...r,
                __uid: r.__uid ?? generateUid(),
                profileName: formData.profileName ?? '',
                firstName: r.firstName ?? '',
                lastName: r.lastName ?? '',
                email: r.email ?? '',
                sms: r.sms ?? ''
            }))
        ];
        const ok = validateRowsAndShow(rowsForValidation);
        if (!ok) { return; }

        const rows = (formData.recipients ?? []) as RecipientRow[];
        const recipients: CreateRecipientRequest[] = buildRecipientsFromRows(rows);

        try {
            const maybePromise = onSubmit({ ...formData, recipients });
            const saved = await Promise.resolve(maybePromise);
            if (saved === false) return;

            setFormData(prev => ({
                ...prev,
                recipients: (prev.recipients ?? []).filter((r: CreateRecipientRequest) => !(r as RecipientRow).isEditing)
            }));
        } catch (err) {
            console.info('Failed to save distribution group', err);
            throw err;
        }
    };

    return (
        <div className='mb-2'>
            <form onSubmit={handleSubmit} className="form">

                <div ref={errorSummaryRef} className='mt-1'>
                    {renderErrorSummaryContent(showErrorSummary, errors, handleCloseStatusMessage)}
                </div>

                <h3 className="heading__title heading__title--distribution-groups">
                    {isEditMode && 'Edit Distribution Group'}
                    {!isEditMode && 'Add Distribution Group'}
                </h3>

                <div className="form__group mt-2">
                    <div className='mb-1'>
                        <label htmlFor="profileName" className="form__label form__label--required">Profile Name</label>
                    </div>
                    <FormField
                        placeholder="Enter distribution group name"
                        name="profileName"
                        type="text"
                        value={formData.profileName}
                        showErrorMessage={false}
                        enableCopyPaste={false}
                        onChange={handleProfileNameChange}
                        disabled={isLoading}
                        error={errors[0]?.profileName}
                    />
                </div>

                <div className="form__section">
                    <h4 className="form__subtitle">Recipients</h4>

                    {renderRecipientsContent({
                        isEditMode,
                        formData,
                        errors,
                        schedules,
                        timeZones,
                        handleRecipientFieldChange,
                        handleRecipientChange,
                        handleRemoveRecipient,
                        sanitizeRecipientRow,
                    })}

                    {(!formData.recipients || formData.recipients.length === 0) && (
                        <p className="text-muted">No recipients added</p>
                    )}

                    <div className="form__section-actions">
                        <button type="button" className="btn btn-add-new" onClick={handleAddRecipient} disabled={isLoading || schedulesLoading}>Add Recipient</button>
                    </div>
                </div>
            </form>
        </div>
    );
};
